import { Paperclip } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { parseAttachmentMarker } from '@shiftagent/shared';

import { CodeBlock } from './CodeBlock';

/** Format an ISO timestamp into a short human-readable time like "3:42 PM" or "Yesterday, 3:42 PM" */
function formatMessageTime(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const timeStr = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  if (diffDays === 0 && now.getDate() === date.getDate()) return timeStr;
  if (diffDays <= 1 && now.getDate() - date.getDate() === 1) return `Yesterday, ${timeStr}`;
  if (diffDays < 7) return `${date.toLocaleDateString(undefined, { weekday: 'short' })}, ${timeStr}`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

interface ScrollState {
  canUp: boolean;
  canDown: boolean;
  thumbTop: number;
  thumbHeight: number;
  showTrack: boolean;
}

/** Scrollable user message content with ambient scroll-position indicators. */
function UserMessageContent({ children }: { children: React.ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<ScrollState>({ canUp: false, canDown: false, thumbTop: 0, thumbHeight: 0, showTrack: false });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const overflows = scrollHeight > clientHeight + 2;
      const thumbH = overflows ? Math.max(12, (clientHeight / scrollHeight) * clientHeight) : 0;
      const trackRange = clientHeight - thumbH - 4; // 2px inset top+bottom
      const scrollRange = scrollHeight - clientHeight;
      const thumbT = scrollRange > 0 ? (scrollTop / scrollRange) * trackRange : 0;
      setScrollState({
        canUp: scrollTop > 2,
        canDown: scrollTop + clientHeight < scrollHeight - 2,
        thumbTop: thumbT,
        thumbHeight: thumbH,
        showTrack: overflows,
      });
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, []);

  return (
    <div className="relative">
      {/* Directional fade: content above */}
      {scrollState.canUp && (
        <div aria-hidden="true" style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 24,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.12), transparent)',
          pointerEvents: 'none', zIndex: 2, borderRadius: '2px 2px 0 0',
        }} />
      )}
      {/* Directional fade: content below */}
      {scrollState.canDown && (
        <div aria-hidden="true" style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 24,
          background: 'linear-gradient(to top, rgba(0,0,0,0.12), transparent)',
          pointerEvents: 'none', zIndex: 2, borderRadius: '0 0 2px 2px',
        }} />
      )}
      {/* Ambient scroll track — always visible when content overflows */}
      {scrollState.showTrack && (
        <>
          {/* Track rail */}
          <div aria-hidden="true" style={{
            position: 'absolute', top: 2, right: 3, bottom: 2, width: 2,
            background: 'var(--sd-color-border)', opacity: 0.22,
            borderRadius: 9999, pointerEvents: 'none', zIndex: 2,
          }} />
          {/* Track thumb — moves with scroll position, no transition to avoid jank */}
          <div aria-hidden="true" style={{
            position: 'absolute', top: 2 + scrollState.thumbTop, right: 3, width: 2,
            height: scrollState.thumbHeight,
            background: 'var(--sd-color-text-muted)', opacity: 0.60,
            borderRadius: 9999, pointerEvents: 'none', zIndex: 3,
          }} />
        </>
      )}
      {/* Scrollable content — native scrollbar suppressed (both Firefox and WebKit) */}
      <div
        ref={scrollRef}
        className="text-sd-text text-sd-sm leading-normal break-words min-w-0 max-h-[120px] overflow-y-auto pr-[10px] sd-scrollbar-none"
      >
        {children}
      </div>
    </div>
  );
}

export type BubbleGroupPosition = 'first' | 'middle' | 'last' | 'solo';

export interface MessageBubbleProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  children?: React.ReactNode;
  /** Position within a group of consecutive same-role bubbles. Controls corner radius continuity. */
  groupPosition?: BubbleGroupPosition;
}

/**
 * Compute border-radius classes based on role and group position.
 *
 * Assistant bubbles are full-width columns. When grouped, BOTH left and right
 * edges flatten at the seams to form a contiguous block — like a newsroom layout.
 *
 * User bubbles are variable-width and right-aligned. When grouped, the right
 * edge flattens at seams.
 *
 * Connection seam uses [3px] — just enough softness to avoid a knife-edge.
 */
function bubbleRadiusClasses(isUser: boolean, position: BubbleGroupPosition): string {
  if (isUser) {
    switch (position) {
      case 'first':  return 'rounded-t-sd-lg rounded-bl-sd-lg rounded-br-[3px]';
      case 'middle': return 'rounded-tl-sd-lg rounded-bl-sd-lg rounded-tr-[3px] rounded-br-[3px]';
      case 'last':   return 'rounded-tl-sd-lg rounded-bl-sd-lg rounded-tr-[3px] rounded-br-sd-sm';
      default:       return 'rounded-t-sd-lg rounded-bl-sd-lg rounded-br-sd-sm';
    }
  }
  // Assistant: both sides flatten at seams (full-width column)
  switch (position) {
    case 'first':  return 'rounded-t-sd-lg rounded-bl-[3px] rounded-br-[3px]';
    case 'middle': return 'rounded-tl-[3px] rounded-bl-[3px] rounded-tr-[3px] rounded-br-[3px]';
    case 'last':   return 'rounded-tl-[3px] rounded-bl-sd-sm rounded-tr-[3px] rounded-br-sd-lg';
    default:       return 'rounded-t-sd-lg rounded-br-sd-lg rounded-bl-sd-sm';
  }
}

export function MessageBubble({ role, content, timestamp, children, groupPosition = 'solo' }: MessageBubbleProps) {
  const isUser = role === 'user';
  const alignmentClass = isUser ? 'justify-end' : 'justify-start';
  // Only parse attachment markers for user messages — assistant content must never be stripped
  const parsed = isUser ? parseAttachmentMarker(content) : { text: content, filenames: null };
  // Normalize Windows-style line endings to prevent double spacing with whitespace-pre-wrap
  const text = isUser ? parsed.text.replace(/\r\n/g, '\n') : parsed.text;
  const filenames = parsed.filenames;
  const showAgentLabel = !isUser && (groupPosition === 'solo' || groupPosition === 'first');

  return (
    <div className={`flex ${alignmentClass} group/msg`}>
      <div
        className={[
          isUser
            ? `bg-sd-primary-subtle shadow-sd-sm ${bubbleRadiusClasses(true, groupPosition)}`
            : `bg-sd-surface border border-sd-border/30 ${bubbleRadiusClasses(false, groupPosition)}`,
          'transition-[box-shadow,color,background-color,border-color] duration-200 px-4 py-2',
          !isUser && 'overflow-hidden',
          isUser ? 'max-w-[85%]' : 'w-[85%]',
          isUser
            ? 'hover:shadow-sd-md'
            : 'hover:shadow-sd-md relative',
        ].join(' ')}
      >
        {/* Agent accent edge — a fine amber line on the left of assistant bubbles */}
        {!isUser && (
          <div className="absolute left-0 top-2 bottom-2 w-[2px] rounded-full bg-sd-warning/25 group-hover/msg:bg-sd-warning/50 transition-colors duration-300" />
        )}

        {/* Agent identity — static label (only on first or solo bubble in a group) */}
        {showAgentLabel && (
          <span data-testid="agent-identity" className="text-[10px] font-medium tracking-wide text-sd-warning/60 mb-0.5 block select-none">
            shiftagent
          </span>
        )}

        {isUser ? (
          <UserMessageContent>
            <span className="whitespace-pre-wrap">{text}</span>
          </UserMessageContent>
        ) : (
          <div className="text-sd-text text-sd-sm leading-normal break-words min-w-0">
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
                a: ({ children, href }) => (
                  <a href={href} className="text-sd-primary underline" target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
                h1: ({ children }) => <h1 className="text-sd-base font-semibold mt-3 mb-1">{children}</h1>,
                h2: ({ children }) => <h2 className="text-sd-base font-semibold mt-3 mb-1">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sd-sm font-semibold mt-2 mb-0.5">{children}</h3>,
                code: ({ children, className }) => {
                  if (className) {
                    return <code className={className}>{children}</code>;
                  }
                  return <code className="bg-sd-surface-elevated rounded-sd-sm px-1.5 py-0.5 text-sd-sm font-mono">{children}</code>;
                },
                ul: ({ children }) => <ul className="ml-4 space-y-1 list-disc marker:text-sd-text-muted/40">{children}</ul>,
                ol: ({ children }) => <ol className="ml-4 space-y-1 list-decimal marker:text-sd-text-muted/40">{children}</ol>,
                li: ({ children }) => <li className="pl-1">{children}</li>,
                blockquote: ({ children }) => (
                  <blockquote className="border-l-2 border-sd-warning/40 pl-3 my-2 text-sd-text-secondary italic">{children}</blockquote>
                ),
                hr: () => <hr className="my-3 border-sd-border/30" />,
                p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
                table: ({ children }) => (
                  <div className="overflow-x-auto my-2 rounded-sd-sm border border-sd-border/40">
                    <table className="border-collapse w-full text-sd-sm">
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead className="bg-sd-surface-elevated/80 border-b border-sd-border/40">{children}</thead>
                ),
                tr: ({ children, ...props }) => {
                  const isHeader = (props as any).node?.parentNode?.tagName === 'thead';
                  return <tr className={isHeader ? '' : 'even:bg-sd-surface-elevated/30 transition-colors hover:bg-sd-surface-elevated/50'}>{children}</tr>;
                },
                th: ({ children }) => (
                  <th className="px-3 py-1.5 font-semibold text-left text-sd-text-secondary text-sd-xs uppercase tracking-wider">{children}</th>
                ),
                td: ({ children }) => (
                  <td className="px-3 py-1.5 border-t border-sd-border/20">{children}</td>
                ),
              }}
            >
              {text}
            </Markdown>
          </div>
        )}

        {/* File attachment indicator — document manifest style */}
        {isUser && filenames && (
          <div data-testid="attachment-indicator" aria-label="Attached files" className="mt-1.5 pt-1.5 border-t border-sd-border/20">
            <div className="flex items-start gap-1.5">
              <Paperclip size={12} aria-hidden="true" className="shrink-0 mt-[2px] text-sd-warning/70" />
              <div className="flex flex-wrap gap-1">
                {filenames.map((name, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-1.5 py-0.5 text-sd-xs text-sd-text-muted bg-sd-surface-elevated/50 rounded-sd-sm border border-sd-border/20 tracking-tight"
                  >
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {children}
        {timestamp && <div className={`text-sd-xs text-sd-text-muted mt-sd-1 opacity-70${isUser ? ' text-right' : ''}`}>{formatMessageTime(timestamp)}</div>}
      </div>
    </div>
  );
}
